/**
 * Vue view containing inputs for logging in with API keys: map key (apiKey)
 * and panorama key (momaKey). These keys are needed to connect to TomTom API
 * to download data for map & panorama views.
 */
<template>
    <div class="columns is-flex-mobile">
        <div class="column is-one-third">
            <div class="content-white is-dark">
                <img src="../assets/logo.png" alt="">
                <div class="login-content">
                    <div class="field">
                        <label class="is-size-8 is-family-primary">
                            {{ $t("message.apiKeyView") }}</label>
                        <AuthInput :value="apiKey"
                            @input="debouncedSetApi($event.target.value)"
                            :error="apiError" :isVerifying="isVerifyingApiKey" />
                        <p
                            class="help is-danger"
                            v-if="apiError">
                            {{ apiError }}
                        </p>
                    </div>
                    <div class="field moma-field">
                        <label class="is-size-8 is-family-primary">
                            {{ $t("message.momaKeyView") }}</label>
                        <AuthInput :value="momaKey"
                            @input="debouncedSetMoma($event.target.value)"
                            :error="momaError" :isVerifying="isVerifyingMomaKey" />
                        <p
                            class="help is-danger"
                            v-if="momaError">
                            {{ momaError }}
                        </p>
                    </div>
                    <button
                        class="button is-primary is-large is-uppercase mt-1"
                        @click="login"
                        :disabled="loginDisabled">
                        {{ $t("message.loginButton") }}
                    </button>
                </div>
            </div>
        </div>
        <div class="column login-background-image"></div>
    </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';

import debounce from 'debounce';

import AuthInput from '../components/AuthInput.vue';

export default {
    name: 'Auth',

    components: { AuthInput },

    data() {
        return { momaData: null };
    },

    computed: {
        ...mapGetters(['isAuthorized']),

        ...mapGetters('authBuffer', [
            'apiKey',
            'apiError',
            'momaKey',
            'momaError',
            'loginDisabled',
        ]),

        ...mapState('authBuffer', ['isVerifyingApiKey', 'isVerifyingMomaKey']),
    },

    methods: {
        ...mapActions('authBuffer', [
            'setApiKey',
            'setApiError',
            'setMomaKey',
            'setMomaError',
        ]),

        ...mapActions(['setKeys']),
        ...mapActions('permissions', ['fetchPermissions']),

        /**
         * Login method, using global Vuex 'setKeys' action
         */
        login() {
            this.setKeys({
                apiKey: this.apiKey,
                momaKey: this.momaKey,
            });
            this.fetchPermissions();
        },
    },

    created() {
        /**
         * Debounced functions initialization:
         * Debounced function limits number of function calls,
         * delaying processing them until user has stopped typing
         * for a predetermined amount of time.
         * Used to limit number of events during user's input actions.
         */
        this.debouncedSetApi = debounce(this.setApiKey, 500);
        this.debouncedSetMoma = debounce(this.setMomaKey, 500);
    },

    watch: {
        /**
         * When global getter isAuthorized changes to true `newValue`
         * (which means that user is logged with his API keys),
         * authBuffer variables are cleared - it's `apiKey` and `momaKey`
         * are both set to null values. Also both errors are cleared and
         * then the window is redirected to main page.
         * @param {boolean} newValue New value of `isAuthorized` global getter
         */
        isAuthorized(newValue) {
            if (newValue) {
                this.setApiKey(null);
                this.setMomaKey(null);
                this.setApiError(null);
                this.setMomaError(null);

                this.$router.replace('/');
            }
        },
    },
};
</script>
